import { Card, Typography, Stack, Chip, Box, Input, Divider, Tooltip } from '@mui/joy'
import { useCallback } from 'react'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import PaidIcon from '@mui/icons-material/Paid'
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation'
import PersonIcon from '@mui/icons-material/Person'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import InfoIcon from '@mui/icons-material/Info'
import { DriverPriceType } from '@numeo/types-core'

// Formatting helpers
export const formatDuration = (hours?: number) => {
    if (!hours) return 'N/A'
    const totalMinutes = Math.round(hours * 60)
    const h = Math.floor(totalMinutes / 60)
    const m = totalMinutes % 60

    if (h === 0) return `${m}m`
    if (m === 0) return `${h}h`
    return `${h}h ${m}m`
}

interface Props {
    driverCostPerMile: string
    setDriverCostPerMile: (value: string) => void
    pricePerGallon: string
    setPricePerGallon: (value: string) => void
    tripLength: string
    setTripLength: (value: string) => void
    setIsRateManuallyEdited: (value: boolean) => void
    ratePerMile: string
    setTotalRate: (value: string) => void
    totalRate: string
    costs: {
        driver: number
        fuel: number
        toll: number
        total: number
        deadhead: number
    }
    formatCurrency: (value: number) => string
    duration?: number
    driverCostType: DriverPriceType
    driverCostValue: number
}

export const CostsCard: React.FC<Props> = ({
    driverCostPerMile,
    setDriverCostPerMile,
    pricePerGallon,
    setPricePerGallon,
    tripLength,
    setTripLength,
    setIsRateManuallyEdited,
    ratePerMile,
    setTotalRate,
    totalRate,
    costs,
    formatCurrency,
    duration,
    driverCostType,
    driverCostValue,
}) => {
    const handleDriverCostChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setDriverCostPerMile(e.target.value)
        },
        [setDriverCostPerMile]
    )

    // Calculate the actual driver cost amount if it's a percentage-based cost
    const calculatePercentageCost = useCallback(() => {
        if (driverCostType !== DriverPriceType.PERCENT) return costs.driver
        return (parseFloat(totalRate) * driverCostValue) / 100
    }, [driverCostType, driverCostValue, costs.driver, totalRate])

    const handleFuelPriceChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setPricePerGallon(e.target.value)
        },
        [setPricePerGallon]
    )

    const handleTripLengthChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const inputValue = e.target.value
            setTripLength(inputValue)

            // Reset manual edit flag when trip length is changed
            setIsRateManuallyEdited(false)

            // Update total rate if we have a valid RPM
            if (inputValue !== '' && !isNaN(parseFloat(inputValue)) && ratePerMile !== '' && !isNaN(parseFloat(ratePerMile))) {
                const tripLengthValue = parseFloat(inputValue)
                const rpmValue = parseFloat(ratePerMile)
                setTotalRate((rpmValue * tripLengthValue).toFixed(2))
            }
        },
        [ratePerMile, setTripLength, setIsRateManuallyEdited, setTotalRate]
    )

    return (
        <Card
            variant="outlined"
            sx={{
                px: 1,
                borderRadius: 'md',
                gap: 0.5,
            }}
        >
            <Typography level="body-xs" fontWeight="bold">
                Costs Breakdown
            </Typography>
            <Stack direction="row" gap={1} justifyContent="space-between">
                <Chip variant="soft" color="primary" size="sm" startDecorator={<AccessTimeIcon fontSize="small" />}>
                    {formatDuration(duration)}
                </Chip>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Input
                        size="sm"
                        value={tripLength}
                        onChange={handleTripLengthChange}
                        endDecorator="miles"
                        slotProps={{
                            input: {
                                type: 'text',
                                sx: { width: '40px' },
                            },
                        }}
                    />
                </Box>
            </Stack>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flex: 1 }}>
                        <PersonIcon fontSize="small" color="primary" />
                        <Typography level="body-sm" sx={{ fontSize: '0.8rem' }}>
                            Driver:
                        </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                        {driverCostType === DriverPriceType.PER_MILE ? (
                            <Input
                                size="sm"
                                sx={{ fontSize: '0.8rem' }}
                                value={driverCostPerMile}
                                onChange={handleDriverCostChange}
                                startDecorator="$"
                                endDecorator="/mi"
                                slotProps={{
                                    input: {
                                        type: 'text',
                                        sx: { width: '35px' },
                                    },
                                }}
                            />
                        ) : (
                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ fontSize: '0.8rem' }}>
                                <Typography level="body-sm">{formatCurrency(calculatePercentageCost())}</Typography>
                                <Tooltip title={`Based on ${driverCostValue}% of load rate (${formatCurrency(parseFloat(totalRate))})`} arrow>
                                    <InfoIcon fontSize="small" color="primary" sx={{ fontSize: '0.9rem', cursor: 'help' }} />
                                </Tooltip>
                            </Stack>
                        )}
                        <Typography level="body-sm" sx={{ fontSize: '0.8rem' }}>
                            {formatCurrency(costs.driver)}
                        </Typography>
                    </Stack>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <LocalGasStationIcon fontSize="small" color="primary" />
                        <Typography level="body-sm" sx={{ fontSize: '0.8rem' }}>
                            Fuel:
                        </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Input
                            size="sm"
                            sx={{ fontSize: '0.8rem' }}
                            value={pricePerGallon}
                            onChange={handleFuelPriceChange}
                            startDecorator="$"
                            endDecorator="/gal"
                            slotProps={{
                                input: {
                                    type: 'text',
                                    sx: { width: '30px' },
                                },
                            }}
                        />
                        <Typography level="body-sm" sx={{ fontSize: '0.8rem' }}>
                            {formatCurrency(costs.fuel)}
                        </Typography>
                    </Stack>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ fontSize: '0.8rem' }}>
                        <PaidIcon fontSize="small" color="warning" />
                        <Typography level="body-sm">Tolls:</Typography>
                    </Stack>
                    <Typography level="body-sm" sx={{ fontSize: '0.8rem' }}>
                        {costs.toll > 0 ? formatCurrency(costs.toll) : 0}
                    </Typography>
                </Box>

                <Divider sx={{ my: 0.5 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <AccountBalanceWalletIcon fontSize="small" color="success" />
                        <Typography level="body-sm" fontWeight="bold">
                            Total:
                        </Typography>
                    </Stack>
                    <Typography level="body-sm" fontWeight="bold" color="success">
                        {formatCurrency(costs.total)}
                    </Typography>
                </Box>
            </Box>
        </Card>
    )
}
